import React, { useEffect, useState, useMemo } from "react";
import { Button, Badge, Container, Card, Row, Col, Table } from "react-bootstrap";
import schoolApi from "../../api/schoolApi";
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { ToastContainer, toast } from "react-toastify";
import { Helmet } from "react-helmet";
import CreateTaskModal from "./CreateTaskModal";
import { usePermissions } from "../contexts/PermissionContext";

const TaskList = () => {
  const [tasks, setTasks] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const { userInfo } = usePermissions();
  const navigate = useNavigate();

  const isAdmin = userInfo?.userrole === 'ADMIN' || userInfo?.userrole === 'SYS_ADMIN';

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const result = await schoolApi.fetchTasks();
      if (result.success) {
        setTasks(result.result);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  };

  const header = useMemo(() => [
    {
      title: "Task Details",
      prop: "title",
      isFilterable: true,
      cell: (row) => (
        <div className="d-flex align-items-center">
          <div className="rounded-3 bg-primary bg-opacity-10 text-primary p-2 me-3 d-none d-md-block">
            <i className="fa-solid fa-list-check"></i>
          </div>
          <div>
            <Link to={`/tasks/view/${row.id}`} className="text-dark fw-bold text-decoration-none hover-primary d-block">
              {row.title}
            </Link>
            <small className="text-muted" style={{ fontSize: '0.7rem' }}>Created: {moment(row.created_at).format("DD MMM YYYY")}</small>
          </div>
        </div>
      )
    },
    ...(isAdmin ? [
      {
        title: "Team",
        prop: "assigned_users",
        cell: (row) => {
          let users = row.assigned_users;
          if (typeof users === 'string') {
            try { users = JSON.parse(users); } catch (e) { users = []; }
          }
          if (!users || users.length === 0) return <small className="text-muted">Unassigned</small>;
          
          return (
            <div className="d-flex align-items-center">
              <div className="avatar-group d-flex align-items-center">
                {users.slice(0, 3).map((user, idx) => (
                  <div 
                    key={idx} 
                    className="avatar-xs rounded-circle bg-light border d-flex align-items-center justify-content-center fw-bold text-primary shadow-sm" 
                    title={user.name}
                    style={{ width: '28px', height: '28px', fontSize: '0.7rem', marginLeft: idx > 0 ? '-8px' : '0', zIndex: 10 - idx }}
                  >
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                ))}
                {users.length > 3 && (
                  <div 
                    className="avatar-xs rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center fw-bold shadow-sm" 
                    style={{ width: '28px', height: '28px', fontSize: '0.6rem', marginLeft: '-8px', zIndex: 5 }}
                  >
                    +{users.length - 3}
                  </div>
                )}
              </div>
            </div>
          );
        }
      }
    ] : []),
    {
      title: "Priority",
      prop: "priority",
      isFilterable: true,
      cell: (row) => {
        const colors = { High: 'danger', Medium: 'warning', Low: 'info' };
        const icon = { High: 'fa-angles-up', Medium: 'fa-angle-up', Low: 'fa-angle-down' };
        return (
          <Badge bg={colors[row.priority] || 'secondary'} className="px-3 py-2 rounded-pill fw-normal" style={{ fontSize: '0.7rem' }}>
            <i className={`fa-solid ${icon[row.priority] || 'fa-minus'} me-1`}></i>
            {row.priority}
          </Badge>
        );
      }
    },
    {
      title: "Timeline",
      prop: "due_date",
      isFilterable: true,
      cell: (row) => (
        <div className="small">
          <div className="text-muted text-uppercase" style={{ fontSize: '0.6rem' }}>Due Date</div>
          <div className={`fw-bold ${moment(row.due_date).isBefore(moment()) ? 'text-danger' : 'text-dark'}`}>
            {row.due_date ? moment(row.due_date).format("DD MMM YYYY") : 'No Date'}
          </div>
        </div>
      )
    },
    {
      title: "Status",
      prop: "status",
      isFilterable: true,
      cell: (row) => {
        const statusColors = {
          Pending: 'warning',
          'In Progress': 'info',
          Submitted: 'primary',
          Completed: 'success',
          Cancelled: 'secondary'
        };
        return (
          <div className="d-flex align-items-center">
            <div className={`rounded-circle bg-${statusColors[row.status] || 'dark'} me-2`} style={{ width: '8px', height: '8px' }}></div>
            <span className="fw-bold small">{row.status}</span>
          </div>
        );
      }
    },
    {
      title: "Action",
      prop: "id",
      cell: (row) => (
        <div className="d-flex gap-1 justify-content-end">
          <Button variant="light" size="sm" className="rounded-circle text-primary border-0 shadow-sm action-btn" onClick={() => navigate(`/tasks/view/${row.id}`)} title="View Details">
            <i className="fa-solid fa-eye"></i>
          </Button>
          {isAdmin && (
            <>
              <Button variant="light" size="sm" className="rounded-circle text-info border-0 shadow-sm action-btn" onClick={() => { setSelectedTask(row); setModalShow(true); }} title="Edit Task">
                <i className="fa-regular fa-pen-to-square"></i>
              </Button>
              <Button variant="light" size="sm" className="rounded-circle text-danger border-0 shadow-sm action-btn" onClick={() => handleDelete(row.id)} title="Delete Task">
                <i className="fa fa-trash"></i>
              </Button>
            </>
          )}
        </div>
      )
    }
  ], [isAdmin]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this task? All assignments and files will be removed.")) {
      try {
        const result = await schoolApi.deleteTask(id);
        if (result.success) {
          toast.success("Task deleted successfully");
          fetchTasks();
          window.dispatchEvent(new Event("taskDeleted"));
          window.dispatchEvent(new Event("refreshNotifications"));
        }
      } catch (error) {
        toast.error("Error deleting task");
      }
    }
  };

  return (
    <Container className="mt-4 pb-5">
      <Helmet>
        <title>Task Management | Dashboard</title>
      </Helmet>
      <ToastContainer autoClose={2000} />
      
      <div className="mb-4 d-flex justify-content-between align-items-end">
        <div>
          <h2 className="fw-bold text-dark mb-1">Task Management</h2>
          <p className="text-muted mb-0">Track and manage your team's assignments and progress.</p>
        </div>
        {isAdmin && (
          <Button variant="primary" className="rounded-pill px-4 py-2 fw-bold shadow-sm border-0" onClick={() => { setSelectedTask(null); setModalShow(true); }}>
            <i className="fa-solid fa-plus-circle me-2"></i>Create New Task
          </Button>
        )}
      </div>

      <Card className="shadow-sm border-0 rounded-4 overflow-hidden">
        <Card.Body className="p-4">
          <DatatableWrapper
            body={tasks}
            headers={header}
            paginationOptionsProps={{
              initialState: {
                rowsPerPage: 10,
                options: [10, 25, 50, 100],
              },
            }}
          >
            <Row className="mb-4 align-items-center">
              <Col xs={12} lg={4}>
                <div className="position-relative">
                  <Filter 
                    placeholder="Search tasks..." 
                    classes={{
                      input: "form-control border-0 bg-light rounded-pill px-4 shadow-none",
                      clearButton: "btn btn-outline-secondary border-0 bg-light rounded-pill px-3 shadow-none"
                    }} 
                  />
                </div>
              </Col>
              <Col xs={12} lg={8} className="d-flex justify-content-end align-items-center gap-3">
                <PaginationOptions
                  alwaysShowPagination={true}
                  labels={{ beforeSelect: "Rows per page:" }}
                  classes={{
                    formGroup: "d-flex align-items-center gap-2 mb-0",
                    formControl: "form-select form-select-sm border bg-white shadow-none rounded-3",
                    formText: "small fw-semibold text-muted mb-0 me-1"
                  }}
                />
              </Col>
            </Row>
            <div className="table-responsive">
              <Table hover className="align-middle custom-list-table">
                <TableHeader className="bg-light border-0" />
                <TableBody />
              </Table>
            </div>
            <div className="mt-4 d-flex justify-content-center">
              <Pagination />
            </div>
          </DatatableWrapper>
        </Card.Body>
      </Card>

      {modalShow && (
        <CreateTaskModal
          show={modalShow}
          onHide={() => setModalShow(false)}
          task={selectedTask}
          refresh={fetchTasks}
        />
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        .hover-primary:hover { color: #0d6efd !important; }
        .avatar-group .avatar-xs { border: 2px solid #fff; }
        .action-btn { width: 32px; height: 32px; padding: 0; transition: all 0.2s; }
        .action-btn:hover { transform: translateY(-2px); box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1) !important; }
        .custom-list-table thead th { 
          border: none; 
          background-color: #f8f9fa; 
          padding: 15px;
          color: #6c757d;
          font-weight: 600;
          text-transform: uppercase;
          font-size: 0.75rem;
          letter-spacing: 0.5px;
        }
        .custom-list-table tbody td { 
          padding: 15px; 
          border-bottom: 1px solid #f1f3f5; 
        }
        .custom-list-table tbody tr:last-child td { border-bottom: none; }
      `}} />
    </Container>
  );
};

export default TaskList;
